import { format, isSameYear } from 'date-fns';
import { sortBy, flatten, uniqBy, isString, isArray, get, isEmpty } from 'lodash';
import { stringify } from 'qs-esm';

import { proxyGet } from '../utils/proxy';
import zonedDate from '../utils/zoned-date';

let _performancesPromise = null;
let _eventsPromise = null;
let _peoplePromise = null;
let _blogsPromise = null;
let _seriesPromise = null;
let _seriesByIdPromise = {};

const now = zonedDate();

export function getPerformances() {
    if (_performancesPromise) {
        return _performancesPromise;
    }

    _performancesPromise = proxyGet(`/event-instances.json`).then(response => {
        let performances = response.docs.map(doc => {
            doc = processPerformance(doc);
            doc = processKeywords(doc);

            doc._auxiliary = {
                group_by_day: format(doc.startDate, 'yyyy-MM-dd'),
                group_by_month: format(doc.startDate, 'yyyy-MM'),
                group_name_month: isSameYear(now, doc.startDate) ? format(doc.startDate, 'MMMM') : format(doc.startDate, 'MMMM yyyy'),
            }

            return doc;
        });

        performances = performances.filter(item => {
            return +item.startDate > +now;
        });

        return sortBy(performances, item => {
            return +item.startDate;
        });
    });

    return _performancesPromise;
}

export function getEvents() {
    if (_eventsPromise) {
        return _eventsPromise;
    }

    _eventsPromise = getPerformances().then(performances => {
        let events = {}

        for (const performance of performances) {
            const event = performance.event;

            if ((event.id in events) === false) {
                events[event.id] = {
                    ...event,
                    filters: {
                        categories: [],
                        events_filters: [],
                        event_type_filters: [],
                        time_of_day_filters: [],
                        genre_filters: [],
                        venue_filters: [],
                        season_filters: [],
                    },
                }
            }

            events[event.id].filters.categories.push(performance.filters.categories);
            events[event.id].filters.events_filters.push(performance.filters.events_filters);
            events[event.id].filters.event_type_filters.push(performance.filters.event_type_filters);
            events[event.id].filters.time_of_day_filters.push(performance.filters.time_of_day_filters);
            events[event.id].filters.genre_filters.push(performance.filters.genre_filters);
            events[event.id].filters.venue_filters.push(performance.filters.venue_filters);
            events[event.id].filters.season_filters.push(performance.filters.season_filters);
        }

        events = Object.values(events);

        events = events.map(event => {
            event.filters.categories = flatten(event.filters.categories);
            event.filters.events_filters = flatten(event.filters.events_filters);
            event.filters.event_type_filters = flatten(event.filters.event_type_filters);
            event.filters.time_of_day_filters = flatten(event.filters.time_of_day_filters);
            event.filters.genre_filters = flatten(event.filters.genre_filters);
            event.filters.venue_filters = flatten(event.filters.venue_filters);
            event.filters.season_filters = flatten(event.filters.season_filters);

            event.filters.categories = uniqBy(event.filters.categories, item => {
                return item.type;
            });

            event.filters.events_filters = uniqBy(event.filters.events_filters, item => {
                return item.type;
            });

            event.filters.event_type_filters = uniqBy(event.filters.event_type_filters, item => {
                return item.type;
            });

            event.filters.time_of_day_filters = uniqBy(event.filters.time_of_day_filters, item => {
                return item.type;
            });

            event.filters.genre_filters = uniqBy(event.filters.genre_filters, item => {
                return item.type;
            });

            event.filters.venue_filters = uniqBy(event.filters.venue_filters, item => {
                return item.type;
            });

            event.filters.season_filters = uniqBy(event.filters.season_filters, item => {
                return item.type;
            });

            return event;
        });

        return sortBy(events, item => {
            return +item.startDate;
        });
    });

    return _eventsPromise;
}

export function getBlogs() {
    if (_blogsPromise) {
        return _blogsPromise
    }

    _blogsPromise = proxyGet(`/api/blogs.json`, null, null, true)
        .then(response => {
            return response.docs;
        });

    return _blogsPromise;
}

export async function getPeople() {
    if (_peoplePromise) {
        return _peoplePromise;
    }

    _peoplePromise = new Promise(async (resolve) => { // eslint-disable-line
        const query = {
            limit: 100,
        }

        let docs = [];
        let page = 1;

        do {
            const stringifiedQuery = stringify({ ...query, page: page }, { addQueryPrefix: true });

            const response = await proxyGet(`/people${stringifiedQuery}`);
            // const response = await fetch(`https://sydneysymphony.devspace.net/api/people${stringifiedQuery}`).then(r => r.json()); // eslint-disable-line

            docs = [ ...docs, ...response.docs ];

            page = response.nextPage;
        } while (page);

        resolve(docs);
    });

    return _peoplePromise;
}

export async function getSeries() {
    if (_seriesPromise) {
        return _seriesPromise;
    }

    _seriesPromise = new Promise(async (resolve) => { // eslint-disable-line
        const query = {
            limit: 100,
        }

        let docs = [];
        let page = 1;

        do {
            const stringifiedQuery = stringify({ ...query, page: page }, { addQueryPrefix: true });

            const response = await proxyGet(`/series.json${stringifiedQuery}`);

            // const response = await fetch(`https://sydneysymphony.devspace.net/api/people${stringifiedQuery}`).then(r => r.json()); // eslint-disable-line

            docs = [ ...docs, ...response.docs ];

            page = response.nextPage;
        } while (page);

        resolve(docs);
    });

    return _peoplePromise;
}

export async function getSeriesById(id) {
    if (_seriesPromise) {
        try {
            const allSeries = await _seriesPromise;
            return allSeries.find(series => series.id === id);
        } catch (error) {
            // fall through to individual fetch
        }
    }

    if (!_seriesByIdPromise[id]) {
        _seriesByIdPromise[id] = proxyGet(`/series/${id}`).then(series => series);
    }

    return _seriesByIdPromise[id];
}

/**
 * Mimic Shopify's handleize function
 *
 * https://github.com/Shopify/liquid/blob/42b6763546f218dc8ad1184e10c5e2d9a262b006/performance/shopify/shop_filter.rb#L97-L106
 */
function handleize(string) {
    if (!string) {
        return '';
    }

    return string
        .toString()
        .toLowerCase()
        .replace(/['"()[]]/g, '')
        .replace(/\W+/g, '-')
        .replace(/-+$/, '')
        .replace(/^-+/, '');
}

function processKeywords(bookableItem) {
    const categories = [];
    const eventTypes = [];
    const event = [];
    const timeOfDays = [];
    let genres = [];
    const venues = [];
    const seasons = [];

    const eventTypesArray = bookableItem.attributes?.custom?.event_type ?? [];
    eventTypesArray.map(type => {
        categories.push(type);
    });

    event.push({ title: bookableItem.event.title });

    categories.length ? categories.map(category => {
        eventTypes.push({ title: category });
    }) : [];

    bookableItem.availability_handle = handleize(bookableItem.availability);

    const timeOfDay = bookableItem.attributes?.custom?.time_of_day;

    if (timeOfDay) {
        timeOfDays.push({ title: timeOfDay });
    }

    let genre = bookableItem.event?.attributes?.custom?.genre;

    if (genre && (isString(genre) || isArray(genre))) {
        if (isString(genre)) {
            genre = [genre];
        }

        genres = genre.map(item => ({
            title: item,
        }))
    }

    if (bookableItem.venue) {
        venues.push({ title: bookableItem.venue.title });
    }

    const season = bookableItem.event?.season?.title;

    if (season) {
        seasons.push({ title: season });
    }

    bookableItem.filters = {
        categories: categories,
        events_filters: event,
        event_type_filters: eventTypes,
        time_of_day_filters: timeOfDays,
        genre_filters: genres,
        venue_filters: venues,
        season_filters: seasons,
    };

    return bookableItem;
}

function processPerformance(bookableItem) {
    bookableItem.venue = bookableItem.venue ?? bookableItem.event.venue ?? null;
    bookableItem.title = bookableItem.event.title;
    bookableItem.prefix = bookableItem.event.prefix;
    bookableItem.startDate = zonedDate(new Date(bookableItem.startDate));
    bookableItem.description = bookableItem.event.description;
    bookableItem.image = bookableItem.event?.image || null;
    bookableItem.availability = bookableItem.attributes?.custom?.availability || null;
    bookableItem.availability_handle = bookableItem.availability?.toLowerCase().replace(/\s/g, '-');

    bookableItem.event.startDate = zonedDate(new Date(bookableItem.event.startDate));
    bookableItem.event.endDate = zonedDate(new Date(bookableItem.event.endDate));

    if (get(bookableItem, 'event.attributes.custom.hide_book_button') !== true) {
        const bookingUrl = get(bookableItem, 'event.attributes.custom.custom_button.url');

        if (!isEmpty(bookingUrl)) {
            const bookingStatus = get(bookableItem, 'event.attributes.custom.custom_button.display_text');

            bookableItem.ctaPrimary = {
                title: isEmpty(bookingStatus) ? 'Buy Tickets' : bookingStatus,
                link: bookingUrl,
            }
        } else if (bookableItem.tessitura.performanceId) {
            bookableItem.ctaPrimary = {
                title: 'Buy Tickets',
                link: `${AppConfig.boDomain}/booking/production/${bookableItem.tessitura.performanceId}`,
            }
        }
    }

    bookableItem.ctaSecondary = bookableItem.event.url ? {
        title: 'More Info',
        link: bookableItem.event.url,
    } : null;

    const date = bookableItem.startDate;
    const hour = format(date, 'h');
    const minutes = format(date, 'mm');
    const period = format(date, 'a').toLowerCase();

    bookableItem.formattedTime = minutes === '00' ? `${hour}${period}` : `${hour}:${minutes}${period}`;

    return bookableItem;
}
